import { useTranslation } from 'next-i18next'

import { useRef } from 'react'

import loginAnalytics from '~/analytics/login/loginAnalytics'
import OrLine from '~/components/OrLine'

import { LoginFields } from '~/auth/types/login.types'

import ButtonGoogleLogin from '~/auth/components/ButtonGoogleLogin'

import { useAppSelector, useAppDispatch } from '~/redux/hooks'

import { selectBackendError, selectFields, setFieldsActionCreator } from '~/auth/slices/login.slice'

import { ButtonPrimary } from '~/components/Buttons'
import { InputV2 } from '~/components/InputV2'
import useCheckIfUserInDb from '~/auth/hooks/useCheckIfUserInDb'

import { useEmailInputValidationLocale } from '../hooks/useEmailInputValidationLocale'

import ButtonFacebookLogin from './ButtonFacebookLogin'

import LoginServerError from './LoginServerError'
import { TermsAndConditions } from './TermsAndConditions'
import { LoginExistingAccount } from './LoginExistingAccount'

export default function Authenticate({ title }: { title?: string }): React.ReactElement {
  const { t } = useTranslation(['logIn', 'authenticate', 'fields'])
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectFields)
  const backendError = useAppSelector(selectBackendError)
  const { handleOnCheckIfUserInDb, isLoading } = useCheckIfUserInDb()
  const ref = useRef<HTMLInputElement>(null)

  const handleOnChangeTextFields = (name: string, value: string): void => {
    // track only when the first character is placed
    if (fields[name as keyof LoginFields] === '' && value !== '') {
      loginAnalytics.writingInForms(name)
    }
    dispatch(setFieldsActionCreator({ ...fields, [name]: value }))
  }

  const handleOnSubmit = (evt: React.FormEvent): void => {
    loginAnalytics.logButtonClick('continue')
    handleOnCheckIfUserInDb(evt)
  }

  useEmailInputValidationLocale(fields.email, ref)

  return (
    <div className="w-full lg:w-125 h-full space-y-5 flex flex-col items-center">
      <h1 className="text-h3 font-bold mb-1 text-center">{title ?? t('authenticate:logInOrSignUp')}</h1>
      <div className="flex gap-3 w-full">
        <ButtonGoogleLogin redirectBack />
        <ButtonFacebookLogin redirectBack />
      </div>
      <OrLine className="my-4" lineColor="bg-black" text={t('registerAccount:orContinue')} textColor="text-black" />

      <LoginServerError message={backendError} />

      <form className="w-full max-w-screen-sm flex flex-col" onSubmit={handleOnSubmit}>
        <InputV2
          autoComplete="email"
          label={t('fields:email')}
          name="email"
          ref={ref}
          setValue={(val) => handleOnChangeTextFields('email', val)}
          type="email"
          value={fields.email}
        />

        <ButtonPrimary
          className="font-semibold border-2 border-white mt-2"
          disabled={!fields.email}
          disableRing
          isFullWidth
          isLoading={isLoading}
          size="small"
          type="submit"
        >
          {t('authenticate:continue')}
        </ButtonPrimary>

        <TermsAndConditions className="w-69" />
      </form>

      <LoginExistingAccount />
    </div>
  )
}
